import { getContactSalesChartData, getContactSalesTotals, getNextContactSubscription } from './contactSalesData';
import type { ContactSalesChartColumn, ContactSalesGrouping, ContactSalesTotals } from './contactSalesData';
import ContactSalesChart, { ContactSalesSeries } from './ContactSalesChart';
import { CurrencyFormat, NumberFormat } from 'App/babel/utils/numberFormat';
import { Title, Text, ButtonSelect, Icon } from '@upsales/components';
import type { NextSubscription } from './contactSalesData';
import IconName from '@upsales/components/Icon/IconName';
import BemClass from '@upsales/components/Utils/bemClass';
import { useStages } from 'App/components/hooks/appHooks';
import React, { useEffect, useState } from 'react';
import T from 'Components/Helpers/translate';
import { useSelector } from 'App/components/hooks';
import { RootState } from 'Store/index';
import logError from 'Helpers/logError';
import moment from 'moment';

import './ContactSales.scss';

type Props = {
	contactId: number;
};

type FigureProps = {
	classes: BemClass;
	icon: IconName;
	iconColor: React.ComponentProps<typeof Icon>['color'];
	label: string;
	value: string;
	/** The relative renewal date under `NEXT SUBSCRIPTION` - Angular's only sub-value. */
	sub?: string;
};

/**
 * One summary figure: a keyed, small-caps label with the value underneath.
 *
 * The order matters. `ClientSales` puts the value first because a company card leads with the
 * number; the Angular contact card led with the label (`.title` above the `.value`, `_contact.scss`)
 * and that is what a rep reading a person's card expects, so it is kept.
 */
const Figure = ({ classes, icon, iconColor, label, value, sub }: FigureProps) => (
	<div className={classes.elem('figure').b()}>
		<div className={classes.elem('figure').elem('label').b()}>
			<Icon name={icon} color={iconColor} />
			<Text size="sm" color="grey-10">
				{label}
			</Text>
		</div>
		<Title>{value}</Title>
		{sub ? (
			<Text size="sm" color="grey-10">
				{sub}
			</Text>
		) : null}
	</div>
);

/**
 * The sales summary + chart on the contact card's sales tab.
 *
 * This is intentionally NOT `ClientSales`. Every figure here is scoped to the contact's own
 * orders and subscriptions. `ClientSales` renders the company's ARR/MRR change and its
 * recurring widgets, and none of those have a contact-scoped source - showing them on a
 * person's card would present account numbers as that person's numbers.
 */
const ContactSales = ({ contactId }: Props) => {
	const classes = new BemClass('ContactSales');
	const { metadata } = useSelector(({ App }: RootState) => App);
	const { SalesModel } = metadata?.params || {};
	const hasCM = SalesModel === 'cm';

	const excludedStageIds = useStages()
		.filter(stage => stage.exclude)
		.map(stage => stage.id);

	const [totals, setTotals] = useState<ContactSalesTotals>({ sales: 0, contributionMargin: 0 });
	const [nextSubscription, setNextSubscription] = useState<NextSubscription | null>(null);
	const [grouping, setGrouping] = useState<ContactSalesGrouping>('year');
	const [series, setSeries] = useState<ContactSalesSeries>('sales');
	const [chartColumns, setChartColumns] = useState<ContactSalesChartColumn[]>([]);
	const [chartLoading, setChartLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		setChartLoading(true);

		getContactSalesChartData(contactId, excludedStageIds, grouping)
			.then(columns => {
				if (!cancelled) {
					setChartColumns(columns);
					setChartLoading(false);
				}
			})
			.catch(err => logError(err, 'Could not get the contact sales chart data'));

		return () => {
			cancelled = true;
		};
	}, [contactId, excludedStageIds.join(','), grouping]);

	useEffect(() => {
		let cancelled = false;

		getContactSalesTotals(contactId, excludedStageIds)
			.then(res => {
				if (!cancelled) {
					setTotals(res);
				}
			})
			.catch(err => logError(err, 'Could not get contact sales totals'));

		getNextContactSubscription(contactId)
			.then(res => {
				if (!cancelled) {
					setNextSubscription(res);
				}
			})
			.catch(err => logError(err, 'Could not get the next contact subscription'));

		return () => {
			cancelled = true;
		};
	}, [contactId, excludedStageIds.join(',')]);

	const summaryClasses = classes.elem('summary');
	const currencyFormat = new CurrencyFormat(metadata?.defaultCurrency.iso);
	const numberFormat = new NumberFormat();
	const contributionMarginRatio = totals.sales ? totals.contributionMargin / totals.sales : 0;

	return (
		<div className={classes.b()}>
			<div className={classes.elem('summary').b()}>
				<Figure
					classes={summaryClasses}
					icon="circle"
					iconColor="bright-green"
					label={T('default.sales12Months')}
					value={currencyFormat.short(totals.sales)}
				/>
				{hasCM ? (
					<>
						<Figure
							classes={summaryClasses}
							icon="circle"
							iconColor="medium-green"
							label={T('default.contributionMargin12Months')}
							value={currencyFormat.short(totals.contributionMargin)}
						/>
						<Figure
							classes={summaryClasses}
							icon="circle"
							iconColor="grey-8"
							label={T('default.contributionMarginRatio12Months')}
							value={numberFormat.percent(contributionMarginRatio)}
						/>
					</>
				) : (
					<Figure
						classes={summaryClasses}
						icon="recurring-order"
						iconColor="bright-green"
						label={T('default.agreementNext')}
						value={
							nextSubscription ? currencyFormat.short(nextSubscription.value) : T('default.noAgreement')
						}
						// Relative, like the Angular card - "what a rep wants to know is how far
						// away the renewal is", not the exact calendar date.
						sub={nextSubscription ? moment(nextSubscription.date).fromNow() : undefined}
					/>
				)}
			</div>
			<div className={classes.elem('graph').b()}>
				<div className={classes.elem('graph').elem('toggles').b()}>
					{hasCM ? (
						<ButtonSelect
							value={series}
							onChange={setSeries}
							options={[
								{ value: 'sales' as const, title: T('default.cashFlow') },
								{ value: 'contributionMargin' as const, title: T('default.contributionMargin') }
							]}
						/>
					) : null}
					<ButtonSelect
						value={grouping}
						onChange={setGrouping}
						options={[
							{ value: 'year' as const, title: T('date.year') },
							{ value: 'month' as const, title: T('date.month') },
							{ value: 'user' as const, title: T('default.user') },
							{ value: 'stage' as const, title: T('default.stage') }
						]}
					/>
				</div>
				<ContactSalesChart columns={chartColumns} series={series} loading={chartLoading} />
			</div>
		</div>
	);
};

export default ContactSales;
